// Host-written rich text (Sotravel Club event descriptions).
//
// The editor (components/common/RichTextEditor, TipTap) produces HTML. Events
// written before it existed hold plain text, sometimes with light Markdown
// (**bold**, "- " bullets, "### " headings) because the old public page
// rendered Markdown. These helpers tell the two apart, convert legacy text
// into editor HTML, turn either into plain text (previews, SEO) and sanitize
// HTML before it is put on a public page.

// Tags that mark a value as HTML. Includes the dangerous ones on purpose, so a
// description containing e.g. <script> goes through the sanitizer rather than
// the legacy Markdown path.
const HTML_TAG_RE =
  /<\/?(?:p|br|hr|div|span|strong|b|em|i|u|s|strike|del|h[1-6]|ul|ol|li|blockquote|a|img|picture|video|audio|source|iframe|script|style|object|embed|svg|math|table|pre|code|font|section|article|form|input|button|link|meta|base|template|noscript)(?=[\s/>])[^>]*>/i;

export const isHtml = (value) => HTML_TAG_RE.test(String(value || ""));

export const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const NAMED_ENTITIES = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " };
const decodeEntities = (text) =>
  text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code) => {
    if (code[0] === "#") {
      const n = code[1] === "x" || code[1] === "X" ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : match;
    }
    const named = NAMED_ENTITIES[code.toLowerCase()];
    return named != null ? named : match;
  });

// Only http(s) and mailto links survive. Control characters are removed
// before the check because browsers ignore them inside a scheme
// ("java\tscript:").
export const safeHref = (href) => {
  const value = String(href || "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "") // eslint-disable-line no-control-regex
    .trim();
  return /^(?:https?:\/\/|mailto:)/i.test(value) ? value : null;
};

// ------------------------------------------------------- legacy → HTML

// **bold** / __bold__ / *italic* / _italic_ on already-escaped text.
const emphasis = (s) =>
  s
    .replace(/\*\*(?=\S)([^*]*?\S)\*\*/g, "<strong>$1</strong>")
    .replace(/__(?=\S)([^_]*?\S)__/g, "<strong>$1</strong>")
    .replace(/(^|[^*\w])\*(?=\S)([^*]*?\S)\*(?![*\w])/g, "$1<em>$2</em>")
    .replace(/(^|[^_\w])_(?=\S)([^_]*?\S)_(?![_\w])/g, "$1<em>$2</em>");

// One line of legacy text → inline HTML: escaped, with [label](url) links,
// bare http(s) URLs and emphasis.
const LINK_RE = /\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)|(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"])/gi;
const inline = (raw) => {
  let out = "";
  let last = 0;
  let m;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(raw))) {
    out += emphasis(escapeHtml(raw.slice(last, m.index)));
    const href = safeHref(m[2] || m[3]);
    const label = m[1] != null ? emphasis(escapeHtml(m[1])) : escapeHtml(m[3]);
    out += href ? `<a href="${escapeHtml(href)}">${label}</a>` : label;
    last = LINK_RE.lastIndex;
  }
  return out + emphasis(escapeHtml(raw.slice(last)));
};

const BULLET_RE = /^[ \t]*(?:[-*+•·▪◦])[ \t]+(.*)$/;
const NUMBER_RE = /^[ \t]*\d+[.)][ \t]+(.*)$/;
const HEADING_RE = /^[ \t]*#{1,6}[ \t]+(.*?)[ \t#]*$/;
const QUOTE_RE = /^[ \t]*>[ \t]?(.*)$/;
const RULE_RE = /^[ \t]*([-*_])(?:[ \t]*\1){2,}[ \t]*$/;

// Plain text / light Markdown → the HTML the editor produces: blank lines
// split paragraphs, single line breaks become <br>, and Markdown bullets,
// numbered lists, headings (all → h3, like the old page), quotes, rules,
// bold/italic and links become their HTML equivalents.
export const legacyToHtml = (text) => {
  const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n");
  const html = [];
  let para = [];
  let quote = [];
  let list = null; // { tag: "ul" | "ol", items: [] }
  const flushPara = () => {
    if (para.length) html.push(`<p>${para.map(inline).join("<br>")}</p>`);
    para = [];
  };
  const flushQuote = () => {
    if (quote.length) html.push(`<blockquote><p>${quote.map(inline).join("<br>")}</p></blockquote>`);
    quote = [];
  };
  const flushList = () => {
    if (list) html.push(`<${list.tag}>${list.items.map((item) => `<li><p>${item.map(inline).join("<br>")}</p></li>`).join("")}</${list.tag}>`);
    list = null;
  };
  const flushAll = () => {
    flushPara();
    flushQuote();
    flushList();
  };
  for (const line of lines) {
    let m;
    if (!line.trim()) {
      flushAll();
    } else if ((m = line.match(HEADING_RE))) {
      flushAll();
      if (m[1].trim()) html.push(`<h3>${inline(m[1].trim())}</h3>`);
    } else if (RULE_RE.test(line)) {
      flushAll();
      html.push("<hr>");
    } else if ((m = line.match(BULLET_RE)) || (m = line.match(NUMBER_RE))) {
      const tag = BULLET_RE.test(line) ? "ul" : "ol";
      flushPara();
      flushQuote();
      if (list && list.tag !== tag) flushList();
      if (!list) list = { tag, items: [] };
      list.items.push([m[1].trim()]);
    } else if ((m = line.match(QUOTE_RE))) {
      flushPara();
      flushList();
      quote.push(m[1].trim());
    } else if (list && /^[ \t]+\S/.test(line)) {
      // Indented line right under a list item continues that item.
      list.items[list.items.length - 1].push(line.trim());
    } else {
      flushQuote();
      flushList();
      para.push(line.trim());
    }
  }
  flushAll();
  return html.join("");
};

// What the editor should start from: HTML as is, legacy text converted.
export const toEditorHtml = (value) => {
  const v = String(value || "");
  if (!v.trim()) return "";
  return isHtml(v) ? v : legacyToHtml(v);
};

// --------------------------------------------------------- → plain text

// HTML → readable plain text (block elements on their own lines, list items
// with "• "). Legacy text is returned unchanged. The result is text: render
// it as a React child, never as HTML.
// Rest of a tag after its name; quoted attribute values may contain ">".
const ATTRS = `(?:[^>"']|"[^"]*"|'[^']*')*`;
const HIDDEN_ELEMENT_RE = /<(script|style|iframe|object|embed|svg|math|template|noscript|textarea|title|head)\b[\s\S]*?<\/\1\s*>/gi;
const BR_RE = new RegExp(`<br\\b${ATTRS}>`, "gi");
const LI_RE = new RegExp(`<li\\b${ATTRS}>\\s*(?:<p\\b${ATTRS}>)?`, "gi");
const BLOCK_RE = new RegExp(`</?(?:p|div|h[1-6]|ul|ol|li|blockquote|hr|pre|section|article|table|tr)\\b${ATTRS}>`, "gi");
const ANY_TAG_RE = new RegExp(`</?[a-z!?][^\\s/>]*${ATTRS}>`, "gi");

export const htmlToPlainText = (value) => {
  const v = String(value || "");
  if (!isHtml(v)) return v;
  const text = v
    .replace(HIDDEN_ELEMENT_RE, " ")
    .replace(BR_RE, "\n")
    .replace(LI_RE, "\n• ")
    .replace(BLOCK_RE, "\n")
    .replace(ANY_TAG_RE, "");
  return decodeEntities(text)
    .replace(/[ \t ]+/g, " ")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .join("\n");
};

// ------------------------------------------------------------- sanitize

const ALLOWED_TAGS = new Set(["P", "BR", "STRONG", "B", "EM", "I", "U", "S", "H2", "H3", "H4", "UL", "OL", "LI", "BLOCKQUOTE", "HR", "A"]);
const RENAMED_TAGS = { H1: "h2", H5: "h4", H6: "h4" };
// Dropped with everything inside them; any other unknown element is unwrapped
// (its text is kept).
const DROPPED_TAGS = new Set([
  "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "IMG", "PICTURE", "VIDEO", "AUDIO", "SOURCE", "TRACK", "SVG", "MATH",
  "TEMPLATE", "NOSCRIPT", "NOEMBED", "NOFRAMES", "FRAME", "FRAMESET", "CANVAS", "MAP", "AREA", "FORM", "INPUT", "BUTTON",
  "SELECT", "OPTION", "TEXTAREA", "LINK", "META", "BASE", "HEAD", "TITLE", "XMP", "PLAINTEXT", "APPLET", "PARAM",
]);
// Unknown block wrappers are unwrapped with a space after them so their text
// doesn't run into the next block.
const BLOCK_WRAPPERS = new Set(["DIV", "SECTION", "ARTICLE", "HEADER", "FOOTER", "MAIN", "ASIDE", "NAV", "PRE", "TABLE", "TR", "TD", "TH", "DL", "DT", "DD", "FIGURE", "FIGCAPTION", "ADDRESS", "DETAILS", "SUMMARY"]);
const MAX_DEPTH = 40;

const copyInto = (from, to, doc, depth) => {
  from.childNodes.forEach((node) => {
    if (node.nodeType === 3) {
      to.appendChild(doc.createTextNode(node.nodeValue));
      return;
    }
    if (node.nodeType !== 1) return; // comments, processing instructions
    const tag = String(node.nodeName).toUpperCase();
    if (DROPPED_TAGS.has(tag)) return;
    if (depth > MAX_DEPTH) {
      to.appendChild(doc.createTextNode(node.textContent || ""));
      return;
    }
    const name = RENAMED_TAGS[tag] || (ALLOWED_TAGS.has(tag) ? tag.toLowerCase() : null);
    if (!name) {
      copyInto(node, to, doc, depth + 1);
      if (BLOCK_WRAPPERS.has(tag)) to.appendChild(doc.createTextNode(" "));
      return;
    }
    const el = doc.createElement(name);
    if (name === "a") {
      const href = safeHref(node.getAttribute("href"));
      if (href) {
        el.setAttribute("href", href);
        if (/^https?:/i.test(href)) {
          el.setAttribute("target", "_blank");
          el.setAttribute("rel", "noopener noreferrer");
        }
      }
    }
    to.appendChild(el);
    if (name !== "br" && name !== "hr") copyInto(node, el, doc, depth + 1);
  });
};

// Strict allowlist sanitizer for host HTML shown on public pages. Builds a new
// tree holding only the allowed tags, only `href` (http/https/mailto) on
// links, and text. The input is parsed with DOMParser, whose documents are
// inert (no scripts run, nothing loads). Without a DOM it falls back to
// escaped plain text.
export const sanitizeRichHtml = (html) => {
  const source = String(html || "");
  if (!source.trim()) return "";
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return htmlToPlainText(source)
      .split("\n")
      .map((line) => `<p>${escapeHtml(line)}</p>`)
      .join("");
  }
  const parser = new window.DOMParser();
  const input = parser.parseFromString(source, "text/html");
  const output = parser.parseFromString("<!doctype html><html><body></body></html>", "text/html");
  const root = output.createElement("div");
  copyInto(input.body, root, output, 0);
  return root.innerHTML;
};
